import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Home, MapPin, Calendar, Clock, Users, CreditCard, Mail, Phone, User, Compass } from "lucide-react";
import { format } from "date-fns";
import logoUrl from "@/assets/logo-aot.png";

const TourBookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    booking: any;
    tourTitle: string;
    tourImage: string | null;
    destinationTitle: string;
    duration: string | null;
    pricingMode: string;
    selectedPackageName: string | null;
    adultPrice: number | null;
    childPrice: number | null;
  } | null;

  if (!state?.booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No booking information found.</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  const { booking } = state;

  const paymentMethodLabels: Record<string, string> = {
    cash: "Cash",
    paypal: "PayPal",
    stripe: "Credit / Debit Card",
  };

  const statusColors: Record<string, string> = {
    pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
    confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
    completed: "bg-green-500/15 text-green-700 border-green-200",
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-hero-bg py-4 border-b border-hero-foreground/10 print:hidden">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <a href="/" className="inline-block">
            <img src={logoUrl} alt="Always On Time Transfers Tours" className="h-12 w-auto rounded-lg" />
          </a>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Success Banner */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
            Tour Booking <span className="text-primary">Confirmed!</span>
          </h1>
          <p className="text-muted-foreground mt-2">Your excursion has been successfully booked.</p>
        </div>

        {/* Voucher Card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg print:shadow-none print:border-2">
          {/* Booking Code Header */}
          <div className="bg-hero-bg px-6 py-5 text-center">
            <p className="text-hero-foreground/60 text-xs uppercase tracking-widest mb-1">Booking Code</p>
            <p className="text-3xl font-display font-bold text-primary tracking-wider">
              {booking.booking_code || "N/A"}
            </p>
          </div>

          {state.tourImage && (
            <div className="h-40 overflow-hidden">
              <img src={state.tourImage} alt={state.tourTitle} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* Tour Info */}
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Tour Details</h3>
              <div className="flex items-start gap-3">
                <Compass className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-display font-bold text-foreground text-lg">{state.tourTitle}</p>
                  {state.destinationTitle && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {state.destinationTitle}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Date & Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {booking.excursion_date ? format(new Date(booking.excursion_date + "T12:00:00"), "PPP") : "N/A"}
                  </p>
                </div>
              </div>
              {booking.schedule && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Schedule</p>
                    <p className="text-sm font-medium text-foreground">{booking.schedule}</p>
                  </div>
                </div>
              )}
              {state.duration && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-sm font-medium text-foreground">{state.duration}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Guests */}
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-primary shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Guests</p>
                <p className="text-sm font-medium text-foreground">
                  {state.pricingMode === "fixed"
                    ? `${booking.adults} Adult(s)${booking.children > 0 ? ` + ${booking.children} Child(ren)` : ""}`
                    : state.selectedPackageName || "Package"}
                </p>
              </div>
            </div>

            {booking.pickup_point && (
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Pickup Point</p>
                  <p className="text-sm font-medium text-foreground">{booking.pickup_point}</p>
                </div>
              </div>
            )}

            <hr className="border-border" />

            {/* Customer Info */}
            <div className="space-y-3">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm text-foreground">{booking.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm text-foreground">{booking.customer_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm text-foreground">{booking.customer_phone}</span>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Payment & Total */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Payment:</span>
                  <span className="text-sm font-medium text-foreground">
                    {paymentMethodLabels[booking.payment_method] || booking.payment_method}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={statusColors[booking.payment_status] || statusColors.pending}>
                    {booking.payment_status === "paid" ? "Paid" : booking.payment_status === "pending" ? "Pending" : "Unpaid"}
                  </Badge>
                  <Badge variant="outline" className={statusColors[booking.status] || statusColors.pending}>
                    {booking.status === "pending" ? "Pending" : booking.status}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total Price</p>
                <p className="text-3xl font-display font-bold text-primary">${booking.total_price}</p>
              </div>
            </div>

            {booking.comments && (
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Comments</p>
                <p className="text-sm text-foreground">{booking.comments}</p>
              </div>
            )}
          </div>
        </div>

        {/* QR Code */}
        {booking.booking_code && (
          <div className="bg-card border border-border rounded-2xl p-6 mt-6 text-center space-y-4">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Verification QR Code</h3>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`${window.location.origin}/voucher/${booking.booking_code}`)}`}
              alt="Voucher QR Code"
              className="mx-auto rounded-lg"
              width={180}
              height={180}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 print:hidden">
          <Button onClick={() => window.print()} variant="outline" className="flex-1">
            <Printer className="w-4 h-4 mr-2" /> Print Voucher
          </Button>
          {booking.booking_code && (
            <Button variant="outline" className="flex-1" asChild>
              <a href={`/voucher/${booking.booking_code}`} target="_blank" rel="noopener noreferrer">
                View Online Voucher
              </a>
            </Button>
          )}
          <Button onClick={() => navigate("/")} className="flex-1 font-bold">
            <Home className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TourBookingConfirmation;
